/**
 * Registry of interactive objects and the per-frame hit test for every active pointer.
 * Handles hover / select for three-mesh-ui blocks (via setState) and for plain meshes, plus
 * finger-touch presses for tracked hands.
 */
import * as THREE from 'three';
import type { Block } from 'three-mesh-ui';
import type { InputManager, Pointer } from '../xr/InputManager.ts';
import type { Locomotion } from '../xr/Locomotion.ts';

export interface Interactive {
  object: THREE.Object3D;
  onSelect?: (pointer: Pointer, point: THREE.Vector3, hit?: THREE.Intersection) => void;
  onSelectEnd?: (pointer: Pointer) => void;
  onHover?: (pointer: Pointer, point: THREE.Vector3, hit?: THREE.Intersection) => void;
  onLeave?: (pointer: Pointer) => void;
  /** three-mesh-ui block to drive idle/hovered/selected states. */
  ui?: Block;
  /** Allow finger-tip presses from tracked hands. */
  touchable?: boolean;
  enabled?: boolean;
}

const TOUCH_DISTANCE = 0.02;

export class InteractiveSystem {
  private items = new Set<Interactive>();
  private hovered = new Map<string, Interactive>();
  private pressed = new Map<string, Interactive>();
  private touching = new Map<string, Interactive>();
  private readonly raycaster = new THREE.Raycaster();
  private readonly box = new THREE.Box3();
  private readonly tmp = new THREE.Vector3();
  private readonly lastHit = new Map<string, THREE.Intersection | undefined>();

  constructor(
    private readonly input: InputManager,
    private readonly locomotion: Locomotion,
  ) {
    input.on('selectstart', (p) => this.onSelectStart(p));
    input.on('selectend', (p) => this.onSelectEnd(p));
    // A gaze-and-pinch pointer selects in the event that creates it: hit-test it first.
    input.beforeSelect((p) => this.hitTest(p, this.targets()));
  }

  private targets(): THREE.Object3D[] {
    const objects: THREE.Object3D[] = [];
    for (const item of this.items) if (item.enabled !== false) objects.push(item.object);
    return objects;
  }

  add(item: Interactive): Interactive {
    this.items.add(item);
    return item;
  }

  remove(item: Interactive): void {
    this.items.delete(item);
    for (const [id, h] of this.hovered) if (h === item) this.hovered.delete(id);
  }

  clear(): void {
    this.items.clear();
    this.hovered.clear();
    this.pressed.clear();
    this.touching.clear();
  }

  private findInteractive(object: THREE.Object3D): Interactive | undefined {
    let current: THREE.Object3D | null = object;
    while (current) {
      for (const item of this.items)
        if (item.object === current && item.enabled !== false) return item;
      current = current.parent;
    }
    return undefined;
  }

  private setUiState(item: Interactive, state: 'idle' | 'hovered' | 'selected'): void {
    if (item.ui && item.ui.currentState !== state) item.ui.setState(state);
  }

  private onSelectStart(p: Pointer): void {
    const item = this.hovered.get(p.id);
    this.locomotion.captureSelect(p, !!item);
    if (!item) return;
    this.pressed.set(p.id, item);
    this.setUiState(item, 'selected');
    const point =
      p.hitDistance !== undefined
        ? p.ray.at(p.hitDistance, new THREE.Vector3())
        : p.ray.origin.clone();
    item.onSelect?.(p, point, this.lastHit.get(p.id));
  }

  private onSelectEnd(p: Pointer): void {
    const item = this.pressed.get(p.id);
    this.pressed.delete(p.id);
    this.locomotion.captureSelect(p, false);
    if (!item) return;
    item.onSelectEnd?.(p);
    this.setUiState(item, this.hovered.get(p.id) === item ? 'hovered' : 'idle');
  }

  /** What a pointer's ray is on: sets its hit distance and hover, with enter and leave. */
  private hitTest(p: Pointer, objects: THREE.Object3D[]): void {
    this.raycaster.set(p.ray.origin, p.ray.direction);
    this.raycaster.far = 24; // the far side of the arc from the entrance
    const hits = objects.length ? this.raycaster.intersectObjects(objects, true) : [];
    let found: Interactive | undefined;
    let hit: THREE.Intersection | undefined;
    for (const h of hits) {
      // three.js raycasts ignore visibility, so hidden panels must be skipped here.
      if (!visibleInTree(h.object)) continue;
      const item = this.findInteractive(h.object);
      if (item) {
        found = item;
        hit = h;
        break;
      }
    }
    p.hitDistance = hit?.distance;
    this.lastHit.set(p.id, hit);
    const previous = this.hovered.get(p.id);
    if (previous && previous !== found) {
      previous.onLeave?.(p);
      if (this.pressed.get(p.id) !== previous) this.setUiState(previous, 'idle');
      this.hovered.delete(p.id);
    }
    if (found && hit) {
      if (previous !== found) {
        this.hovered.set(p.id, found);
        if (this.pressed.get(p.id) !== found) this.setUiState(found, 'hovered');
      }
      found.onHover?.(p, hit.point, hit);
    }
  }

  update(): void {
    const objects = this.targets();
    // A pointer that went away (a released gaze-and-pinch) no longer hovers anything.
    for (const [id, item] of this.hovered) {
      const p = this.input.pointers.find((q) => q.id === id);
      if (p?.active) continue;
      if (p) item.onLeave?.(p);
      if (this.pressed.get(id) !== item) this.setUiState(item, 'idle');
      this.hovered.delete(id);
      this.lastHit.delete(id);
    }

    for (const p of this.input.activePointers()) {
      this.hitTest(p, objects);

      // Finger-tip touch for tracked hands.
      if (p.kind === 'hand' && p.indexTip) {
        const current = this.touching.get(p.id);
        let touched: Interactive | undefined;
        for (const item of this.items) {
          if (!item.touchable || item.enabled === false || !visibleInTree(item.object)) continue;
          this.box.setFromObject(item.object);
          if (this.box.isEmpty()) continue;
          this.box.expandByScalar(TOUCH_DISTANCE);
          if (this.box.containsPoint(p.indexTip)) {
            touched = item;
            break;
          }
        }
        if (touched && touched !== current) {
          this.touching.set(p.id, touched);
          this.setUiState(touched, 'selected');
          touched.onSelect?.(p, this.tmp.copy(p.indexTip));
          touched.onSelectEnd?.(p);
          setTimeout(() => this.setUiState(touched!, 'idle'), 120);
        } else if (!touched && current) {
          this.touching.delete(p.id);
        }
      }
    }
  }
}

/** True when the object and every ancestor are visible (three.js raycasts do not check this). */
export function visibleInTree(object: THREE.Object3D): boolean {
  let current: THREE.Object3D | null = object;
  while (current) {
    if (!current.visible) return false;
    current = current.parent;
  }
  return true;
}
